import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Form, Modal, Row, Table } from 'react-bootstrap';
import { FaArrowUp, FaRegStar, FaSort, FaSortDown, FaSortUp, FaStar, FaThumbtack } from 'react-icons/fa';
import './Screen.css';
import {
  getScreenState,
  createScreen as createScreenApi,
  renameScreen as renameScreenApi,
  deleteScreen as deleteScreenApi,
  reorderScreens,
  addScreenTicker,
  starScreenTicker,
  unstarScreenTicker,
  addReadylistTicker,
  addLeaderTicker,
  deleteScreenTicker as deleteScreenTickerApi
} from '../services/api';
import { ThemeContext } from '../contexts/ThemeContext';

const SORT_STORAGE_KEY = 'screenSortConfig';
const DEFAULT_SORT_CONFIG = { column: 'ticker', direction: 'asc' };
const PIN_STORAGE_KEY = 'screenPinnedColumns';
const EMPTY_COLUMN_CONTEXT_MENU = { show: false, x: 0, y: 0, screen: null };
const EMPTY_ACTION_CONTEXT_MENU = { show: false, x: 0, y: 0, ticker: null };

const loadSortConfig = () => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_SORT_CONFIG };
  }
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_SORT_CONFIG };
    }
    const parsed = JSON.parse(raw);
    const validColumn =
      parsed?.column &&
      (['ticker', 'total'].includes(parsed.column) || parsed.column.startsWith('screen:'));
    if (validColumn && ['asc', 'desc'].includes(parsed.direction)) {
      return parsed;
    }
  } catch (error) {
    console.error('Failed to parse Screen sort config', error);
  }
  return { ...DEFAULT_SORT_CONFIG };
};

const loadPinnedColumns = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = localStorage.getItem(PIN_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  } catch (error) {
    console.error('Failed to parse pinned screen config', error);
  }
  return {};
};

const Screen = () => {
  const {
    effectiveTheme,
    weatherThemeEnabled,
    selectedWeatherTheme
  } = useContext(ThemeContext);
  const isDark = effectiveTheme === 'dark';
  const [screens, setScreens] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [stars, setStars] = useState({});
  const [screenModal, setScreenModal] = useState({
    show: false,
    mode: 'create',
    screenId: null,
    name: ''
  });
  const [screenError, setScreenError] = useState('');
  const [tickerError, setTickerError] = useState('');
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState(() => loadSortConfig());
  const [draggingScreenId, setDraggingScreenId] = useState(null);
  const [dragOverScreenId, setDragOverScreenId] = useState(null);
  const [tickerQuery, setTickerQuery] = useState('');
  const [resettingStars, setResettingStars] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetError, setResetError] = useState('');
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [pinnedScreens, setPinnedScreens] = useState(() => loadPinnedColumns());
  const [columnContextMenu, setColumnContextMenu] = useState(EMPTY_COLUMN_CONTEXT_MENU);
  const [actionContextMenu, setActionContextMenu] = useState(EMPTY_ACTION_CONTEXT_MENU);

  useEffect(() => {
    if (!actionMessage.text) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      setActionMessage({ type: '', text: '' });
    }, 4000);
    return () => clearTimeout(timeoutId);
  }, [actionMessage]);

  const toKey = (value) => (value != null ? value.toString() : '');

  const normalizeStars = (raw) => {
    if (!raw || typeof raw !== 'object') {
      return {};
    }
    return Object.entries(raw).reduce((acc, [tickerId, screenMap]) => {
      const tickerKey = toKey(tickerId);
      const normalizedScreens = {};
      if (screenMap && typeof screenMap === 'object') {
        Object.keys(screenMap).forEach((screenId) => {
          normalizedScreens[toKey(screenId)] = true;
        });
      }
      acc[tickerKey] = normalizedScreens;
      return acc;
    }, {});
  };

  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(sortConfig));
  }, [sortConfig]);

  useEffect(() => {
    localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(pinnedScreens));
  }, [pinnedScreens]);

  const isScreenPinned = (screenId) => Boolean(pinnedScreens[toKey(screenId)]);

  const closeColumnContextMenu = useCallback(() => setColumnContextMenu(EMPTY_COLUMN_CONTEXT_MENU), []);
  const closeActionContextMenu = useCallback(() => setActionContextMenu(EMPTY_ACTION_CONTEXT_MENU), []);

  const handleColumnContextMenu = (event, screen) => {
    event.preventDefault();
    if (!screen) return;

    const MENU_WIDTH = 180;
    const MENU_HEIGHT = 100;
    const PADDING = 8;
    let posX = event.clientX;
    let posY = event.clientY;

    if (posX + MENU_WIDTH > window.innerWidth) {
      posX = window.innerWidth - MENU_WIDTH - PADDING;
    }
    if (posY + MENU_HEIGHT > window.innerHeight) {
      posY = window.innerHeight - MENU_HEIGHT - PADDING;
    }

    setColumnContextMenu({
      show: true,
      x: Math.max(posX, PADDING),
      y: Math.max(posY, PADDING),
      screen
    });
  };

  useEffect(() => {
    if (!columnContextMenu.show) {
      return undefined;
    }

    const handleGlobalClick = () => closeColumnContextMenu();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeColumnContextMenu();
      }
    };

    window.addEventListener('click', handleGlobalClick);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('click', handleGlobalClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [columnContextMenu.show, closeColumnContextMenu]);

  const handleActionContextMenu = (event, ticker) => {
    event.preventDefault();
    if (!ticker) return;

    const MENU_WIDTH = 200;
    const MENU_HEIGHT = 140;
    const PADDING = 8;
    let posX = event.clientX;
    let posY = event.clientY;

    if (posX + MENU_WIDTH > window.innerWidth) {
      posX = window.innerWidth - MENU_WIDTH - PADDING;
    }
    if (posY + MENU_HEIGHT > window.innerHeight) {
      posY = window.innerHeight - MENU_HEIGHT - PADDING;
    }

    setActionContextMenu({
      show: true,
      x: Math.max(posX, PADDING),
      y: Math.max(posY, PADDING),
      ticker
    });
  };

  useEffect(() => {
    if (!actionContextMenu.show) {
      return undefined;
    }

    const handleGlobalClick = () => closeActionContextMenu();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeActionContextMenu();
      }
    };

    window.addEventListener('click', handleGlobalClick);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('click', handleGlobalClick);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [actionContextMenu.show, closeActionContextMenu]);

  const toggleScreenPin = (screenId) => {
    const key = toKey(screenId);
    setPinnedScreens((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  useEffect(() => {
    const loadState = async () => {
      setLoading(true);
      await fetchScreenState();
      setLoading(false);
    };
    loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScreenState = async () => {
    try {
      const response = await getScreenState();
      setScreens(response.data?.screens || []);
      setTickers(response.data?.tickers || []);
      setStars(normalizeStars(response.data?.stars));
    } catch (error) {
      console.error('Failed to load Screen data', error);
      setApiError(error.response?.data?.error || 'Failed to load screen data');
    }
  };

  const toggleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: column === 'total' ? 'desc' : 'asc' };
    });
  };

  const openScreenModal = (mode = 'create', screen = null) => {
    setScreenError('');
    setScreenModal({
      show: true,
      mode,
      screenId: screen?.id ?? null,
      name: mode === 'edit' ? (screen?.name || '') : ''
    });
  };

  const closeScreenModal = () => {
    setScreenError('');
    setScreenModal((prev) => ({ ...prev, show: false }));
  };

  const handleScreenSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = screenModal.name.trim();
    if (!trimmedName) {
      setScreenError('Screen name is required');
      return;
    }

    try {
      if (screenModal.mode === 'edit') {
        const response = await renameScreenApi(screenModal.screenId, trimmedName);
        setScreens((prev) =>
          prev.map((screen) =>
            screen.id === screenModal.screenId ? response.data.screen : screen
          )
        );
      } else {
        const response = await createScreenApi(trimmedName);
        setScreens((prev) => [...prev, response.data.screen]);
      }
      closeScreenModal();
    } catch (error) {
      console.error('Failed to save screen', error);
      setScreenError(error.response?.data?.error || 'Failed to save screen');
    }
  };

  const handlePromoteToReadylist = async (ticker) => {
    const normalizedSymbol = (ticker?.symbol || '').trim().toUpperCase();
    if (!normalizedSymbol) {
      setActionMessage({ type: 'error', text: 'Ticker symbol is missing' });
      return;
    }
    setActionMessage({ type: '', text: '' });
    try {
      await addReadylistTicker(normalizedSymbol);
      setActionMessage({ type: 'success', text: `${normalizedSymbol} added to Readylist` });
    } catch (error) {
      console.error('Failed to promote ticker to Readylist', error);
      const errorMessage = error.response?.data?.error || 'Failed to promote ticker to Readylist';
      setActionMessage({ type: 'error', text: errorMessage });
    } finally {
      closeActionContextMenu();
    }
  };

  const handlePromoteToLeaders = async (ticker) => {
    const normalizedSymbol = (ticker?.symbol || '').trim().toUpperCase();
    if (!normalizedSymbol) {
      setActionMessage({ type: 'error', text: 'Ticker symbol is missing' });
      return;
    }
    setActionMessage({ type: '', text: '' });
    try {
      await addLeaderTicker(normalizedSymbol);
      setActionMessage({ type: 'success', text: `${normalizedSymbol} added to Leaders` });
    } catch (error) {
      console.error('Failed to promote ticker to Leaders', error);
      const errorMessage = error.response?.data?.error || 'Failed to promote ticker to Leaders';
      setActionMessage({ type: 'error', text: errorMessage });
    } finally {
      closeActionContextMenu();
    }
  };

  const handleDeleteTickerRow = async (tickerId) => {
    try {
      await deleteScreenTickerApi(tickerId);
      setTickers((prev) => prev.filter((ticker) => ticker.id !== tickerId));
      setStars((prev) => {
        const next = { ...prev };
        delete next[toKey(tickerId)];
        return next;
      });
      setActionMessage({ type: 'success', text: 'Ticker removed from Screen' });
    } catch (error) {
      console.error('Failed to delete ticker', error);
      setActionMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete ticker'
      });
    } finally {
      closeActionContextMenu();
    }
  };

  const handleDeleteScreen = async (screenId) => {
    try {
      await deleteScreenApi(screenId);
      setScreens((prev) => prev.filter((screen) => screen.id !== screenId));
      setStars((prev) => {
        const screenKey = toKey(screenId);
        const next = {};
        Object.entries(prev).forEach(([tickerId, screenMap]) => {
          if (!screenMap[screenKey]) {
            next[tickerId] = screenMap;
            return;
          }
          const { [screenKey]: _removed, ...rest } = screenMap;
          if (Object.keys(rest).length > 0) {
            next[tickerId] = rest;
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Failed to delete screen', error);
      setApiError(error.response?.data?.error || 'Failed to delete screen');
    }
  };

  const persistScreenOrder = async (orderedScreens) => {
    try {
      await reorderScreens(orderedScreens.map((screen) => screen.id));
    } catch (error) {
      console.error('Failed to reorder screens', error);
      setApiError(error.response?.data?.error || 'Failed to reorder screens');
      await fetchScreenState();
    }
  };

  const handleTickerInputChange = (event) => {
    setTickerQuery(event.target.value);
    if (tickerError) {
      setTickerError('');
    }
  };

  const handleTickerClear = () => {
    setTickerQuery('');
    setTickerError('');
  };

  const handleTickerFormSubmit = async (event) => {
    event.preventDefault();
    const trimmedSymbol = tickerQuery.trim().toUpperCase();
    if (!trimmedSymbol) {
      setTickerError('Ticker symbol is required');
      return;
    }

    try {
      const response = await addScreenTicker(trimmedSymbol);
      setTickers((prev) => [...prev, response.data.ticker]);
      setTickerQuery(trimmedSymbol);
      setTickerError('');
      setActionMessage({ type: 'success', text: `${trimmedSymbol} added to Screen list` });
    } catch (error) {
      console.error('Failed to add ticker', error);
      const errorMessage = error.response?.data?.error || 'Failed to add ticker';
      setTickerError(errorMessage);
      setActionMessage({ type: 'error', text: errorMessage });
      if (error.response?.data?.ticker) {
        const existing = error.response.data.ticker;
        setTickers((prev) => {
          if (prev.some((ticker) => ticker.id === existing.id)) {
            return prev;
          }
          return [...prev, existing];
        });
      }
    }
  };

  const toggleStar = async (tickerId, screenId) => {
    const tickerKey = toKey(tickerId);
    const screenKey = toKey(screenId);
    const isStarred = Boolean(stars[tickerKey]?.[screenKey]);
    try {
      if (isStarred) {
        await unstarScreenTicker(tickerId, screenId);
      } else {
        await starScreenTicker(tickerId, screenId);
      }
      setStars((prev) => {
        const tickerStars = { ...(prev[tickerKey] || {}) };
        if (isStarred) {
          delete tickerStars[screenKey];
        } else {
          tickerStars[screenKey] = true;
        }
        const next = { ...prev };
        if (Object.keys(tickerStars).length === 0) {
          delete next[tickerKey];
        } else {
          next[tickerKey] = tickerStars;
        }
        return next;
      });
    } catch (error) {
      console.error('Failed to toggle star', error);
      setApiError(error.response?.data?.error || 'Failed to update star');
    }
  };


  const openResetConfirmation = () => {
    if (!hasActiveStars || resettingStars) {
      return;
    }
    setResetError('');
    setShowResetConfirm(true);
  };

  const closeResetConfirmation = () => {
    if (resettingStars) {
      return;
    }
    setShowResetConfirm(false);
    setResetError('');
  };

  const handleResetStarsConfirm = async () => {
    if (resettingStars) {
      return;
    }

    const assignments = [];
    Object.entries(stars).forEach(([tickerId, screenMap]) => {
      if (!screenMap) {
        return;
      }
      Object.keys(screenMap).forEach((screenId) => {
        const parsedTickerId = Number.parseInt(tickerId, 10);
        const parsedScreenId = Number.parseInt(screenId, 10);
        if (Number.isNaN(parsedTickerId) || Number.isNaN(parsedScreenId)) {
          return;
        }
        assignments.push({
          tickerId: parsedTickerId,
          screenId: parsedScreenId
        });
      });
    });

    if (assignments.length === 0) {
      setShowResetConfirm(false);
      return;
    }

    setResettingStars(true);
    setResetError('');
    setApiError('');

    try {
      const results = await Promise.allSettled(
        assignments.map(({ tickerId, screenId }) =>
          unstarScreenTicker(tickerId, screenId)
        )
      );

      const hasFailure = results.some((result) => result.status === 'rejected');
      if (hasFailure) {
        console.error('Failed to reset all stars', results);
        const failureMessage = 'Failed to reset all stars. Please try again.';
        setResetError(failureMessage);
        setApiError(failureMessage);
        await fetchScreenState();
        return;
      }

      setStars({});
      setShowResetConfirm(false);
    } catch (error) {
      console.error('Unexpected error while resetting stars', error);
      const message = error.response?.data?.error || 'Failed to reset stars';
      setResetError(message);
      setApiError(message);
      await fetchScreenState();
    } finally {
      setResettingStars(false);
    }
  };

  const handleScreenDragStart = (event, screenId) => {
    if (isScreenPinned(screenId)) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'move';
    setDraggingScreenId(screenId);
    setDragOverScreenId(screenId);
  };

  const handleScreenDragEnter = (event, screenId) => {
    if (
      !draggingScreenId ||
      draggingScreenId === screenId ||
      isScreenPinned(screenId) ||
      isScreenPinned(draggingScreenId)
    ) {
      return;
    }
    event.preventDefault();
    setDragOverScreenId(screenId);
  };

  const handleScreenDragOver = (event, screenId) => {
    if (
      draggingScreenId &&
      draggingScreenId !== screenId &&
      !isScreenPinned(screenId) &&
      !isScreenPinned(draggingScreenId)
    ) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    }
  };

  const handleScreenDrop = (event, screenId) => {
    if (
      !draggingScreenId ||
      draggingScreenId === screenId ||
      isScreenPinned(screenId) ||
      isScreenPinned(draggingScreenId)
    ) {
      return;
    }
    event.preventDefault();

    setScreens((prev) => {
      const pinnedKeys = new Set(
        Object.entries(pinnedScreens)
          .filter(([, value]) => value)
          .map(([key]) => key)
      );
      const fromIndex = prev.findIndex((screen) => screen.id === draggingScreenId);
      const toIndex = prev.findIndex((screen) => screen.id === screenId);
      if (fromIndex === -1 || toIndex === -1) {
        return prev;
      }

      const hasPinnedBarrier = () => {
        if (fromIndex < toIndex) {
          for (let i = fromIndex + 1; i <= toIndex; i += 1) {
            const candidate = prev[i];
            if (candidate && pinnedKeys.has(toKey(candidate.id))) {
              return true;
            }
          }
        } else {
          for (let i = toIndex; i < fromIndex; i += 1) {
            const candidate = prev[i];
            if (candidate && pinnedKeys.has(toKey(candidate.id))) {
              return true;
            }
          }
        }
        return false;
      };

      if (hasPinnedBarrier()) {
        return prev;
      }

      const reordered = [...prev];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      persistScreenOrder(reordered);
      return reordered;
    });

    setDraggingScreenId(null);
    setDragOverScreenId(null);
  };

  const handleScreenDragEnd = () => {
    setDraggingScreenId(null);
    setDragOverScreenId(null);
  };

  const screenColumnWidthPercent = Math.max(0, (100 - 35) / Math.max(screens.length || 1, 1));
  const tableColumnCount = screens.length + 5;

  const tickerRows = useMemo(() => {
    const withTotals = tickers.map((ticker) => {
      const tickerKey = toKey(ticker.id);
      const totalStars = screens.reduce(
        (acc, screen) => acc + (stars[tickerKey]?.[toKey(screen.id)] ? 1 : 0),
        0
      );
      return { ...ticker, totalStars };
    });

    const sorted = [...withTotals];
    if (sortConfig.column === 'ticker') {
      sorted.sort((a, b) => {
        if (a.symbol === b.symbol) {
          return 0;
        }
        const comparison = a.symbol.localeCompare(b.symbol);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    } else if (sortConfig.column === 'total') {
      sorted.sort((a, b) => {
        const comparison = a.totalStars - b.totalStars;
        if (comparison === 0) {
          return a.symbol.localeCompare(b.symbol);
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    } else if (sortConfig.column?.startsWith('screen:')) {
      const [, sortScreenIdRaw] = sortConfig.column.split(':');
      const sortScreenId = toKey(sortScreenIdRaw);
      sorted.sort((a, b) => {
        const aStar = stars[toKey(a.id)]?.[sortScreenId] ? 1 : 0;
        const bStar = stars[toKey(b.id)]?.[sortScreenId] ? 1 : 0;
        let comparison = aStar - bStar;
        if (comparison === 0) {
          comparison = a.symbol.localeCompare(b.symbol);
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    return sorted;
  }, [tickers, sortConfig, stars, screens]);

  const normalizedTickerQuery = tickerQuery.trim().toLowerCase();
  const filteredTickerRows = useMemo(() => {
    if (!normalizedTickerQuery) {
      return tickerRows;
    }
    return tickerRows.filter(
      (ticker) => ticker.symbol.toLowerCase().includes(normalizedTickerQuery)
    );
  }, [tickerRows, normalizedTickerQuery]);

  const activeStarCount = useMemo(() => {
    return Object.values(stars).reduce((total, screenMap) => {
      if (!screenMap) {
        return total;
      }
      return total + Object.keys(screenMap).length;
    }, 0);
  }, [stars]);

  const hasActiveStars = activeStarCount > 0;

  const seasonalModalBackground = useMemo(() => {
    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(231, 245, 255, 0.95)';
    }
    const opacity = isDark ? 0.35 : 0.18;
    switch (selectedWeatherTheme) {
      case 'spring':
        return `rgba(40, 167, 69, ${opacity})`;
      case 'summer':
        return `rgba(23, 162, 184, ${opacity})`;
      case 'autumn':
      case 'fall':
        return `rgba(255, 193, 7, ${opacity})`;
      case 'winter':
        return isDark ? 'rgba(248, 249, 250, 0.12)' : 'rgba(108, 117, 125, 0.18)';
      default:
        return isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(231, 245, 255, 0.95)';
    }
  }, [isDark, selectedWeatherTheme, weatherThemeEnabled]);

  const resetModalStyle = useMemo(
    () => ({
      '--screen-reset-seasonal': seasonalModalBackground,
      '--screen-reset-border': isDark ? 'rgba(148, 163, 184, 0.45)' : 'rgba(148, 163, 184, 0.25)',
      '--screen-reset-header-bg': isDark ? 'rgba(15, 23, 42, 0.85)' : 'rgba(248, 250, 252, 0.92)',
      '--screen-reset-text': isDark ? '#f8fafc' : '#0f172a',
      '--screen-reset-subtle': isDark ? '#b8c5dd' : '#475569'
    }),
    [isDark, seasonalModalBackground]
  );

  const renderSortIcon = (column) => {
    if (sortConfig.column !== column) {
      return <FaSort className="sort-icon" aria-hidden="true" />;
    }
    return sortConfig.direction === 'asc' ? (
      <FaSortUp className="sort-icon" aria-hidden="true" />
    ) : (
      <FaSortDown className="sort-icon" aria-hidden="true" />
    );
  };

  return (
    <Container className="screen-page">
      <Row className="mb-3 gy-3 align-items-start">
        <Col xs={12} lg>
          <form className="screen-ticker-form" onSubmit={handleTickerFormSubmit}>
            <div className="screen-ticker-input-stack">
              <div className="screen-ticker-input-wrapper">
                <input
                  type="text"
                  className="screen-ticker-input"
                  placeholder="Filter or add ticker (e.g. AAPL)"
                  value={tickerQuery}
                  onChange={handleTickerInputChange}
                  maxLength={16}
                />
              </div>
              <button
                type="button"
                className="screen-action-btn screen-clear-btn"
                onClick={handleTickerClear}
                disabled={!tickerQuery}
              >
                Clear
              </button>
              <button
                type="submit"
                className="screen-action-btn screen-add-btn"
                disabled={!tickerQuery.trim()}
              >
                Add
              </button>
            </div>
            {tickerError && <div className="screen-ticker-error">{tickerError}</div>}
          </form>
        </Col>
        <Col xs={12} lg="auto" className="d-flex justify-content-end">
          <div className="d-flex flex-wrap justify-content-end gap-2 screen-actions w-100">
            <button
              type="button"
              className="screen-action-btn screen-add-btn ms-lg-auto"
              onClick={() => openScreenModal('create')}
            >
              + Screen
            </button>
            <button
              type="button"
              className="screen-action-btn screen-reset-btn"
              onClick={openResetConfirmation}
              disabled={!hasActiveStars || resettingStars}
            >
              {resettingStars ? 'Resetting...' : 'Reset'}
            </button>
          </div>
        </Col>
      </Row>

      {apiError && (
        <Row className="mb-2">
          <Col>
            <div className="screen-ticker-error">{apiError}</div>
          </Col>
        </Row>
      )}

      {actionContextMenu.show && (
        <div
          className="screen-context-menu"
          style={{ top: `${actionContextMenu.y}px`, left: `${actionContextMenu.x}px` }}
          role="menu"
        >
          <button
            type="button"
            className="dropdown-item"
            onClick={() => handlePromoteToReadylist(actionContextMenu.ticker)}
          >
            Promote to Readylist
          </button>
          <button
            type="button"
            className="dropdown-item"
            onClick={() => handlePromoteToLeaders(actionContextMenu.ticker)}
          >
            Promote to Leaders
          </button>
          <button
            type="button"
            className="dropdown-item text-danger"
            onClick={() => handleDeleteTickerRow(actionContextMenu.ticker.id)}
          >
            Delete
          </button>
        </div>
      )}

      <div className="screen-toast-region" aria-live="polite" aria-atomic="true">
        {actionMessage.text && (
          <div
            className={`screen-toast ${
              actionMessage.type === 'error' ? 'screen-toast-error' : 'screen-toast-success'
            }`}
            role="status"
          >
            {actionMessage.text}
          </div>
        )}
      </div>

      <div className="tickers-table-container" style={{ '--seasonal-bg': 'var(--bs-body-bg)' }}>
        <div className="table-responsive">
          <Table
            hover
            responsive
            size="sm"
            className="ticker-table tickers-table screen-table align-middle"
            style={{
              '--screen-count': Math.max(screens.length, 1),
              '--screen-static-width': '360px'
            }}
          >
            <thead>
              <tr>
                <th className="screen-index-cell">#</th>
                <th>
                  <button
                    type="button"
                    className="sort-button"
                    onClick={() => toggleSort('ticker')}
                  >
                    Ticker {renderSortIcon('ticker')}
                  </button>
                </th>
                {screens.map((screen) => {
                  const pinned = isScreenPinned(screen.id);
                  const columnClasses = [
                    'screen-column',
                    dragOverScreenId === screen.id && draggingScreenId ? 'screen-column-drag-over' : '',
                    pinned ? 'screen-column-fixed' : ''
                  ]
                    .filter(Boolean)
                    .join(' ');
                  const headerClasses = [
                    'screen-column-header',
                    draggingScreenId === screen.id ? 'screen-column-dragging' : '',
                    pinned ? 'screen-column-pinned' : ''
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <th
                      key={screen.id}
                      className={columnClasses}
                      style={{ width: `${screenColumnWidthPercent}%` }}
                      onDragOver={(event) => handleScreenDragOver(event, screen.id)}
                      onDrop={(event) => handleScreenDrop(event, screen.id)}
                      onContextMenu={(event) => handleColumnContextMenu(event, screen)}
                    >
                      <div
                        className={headerClasses}
                        draggable={!pinned}
                        aria-disabled={pinned}
                        onDragStart={(event) => handleScreenDragStart(event, screen.id)}
                        onDragEnter={(event) => handleScreenDragEnter(event, screen.id)}
                        onDragEnd={handleScreenDragEnd}
                      >
                        <button
                          type="button"
                          className="sort-button screen-column-sort-button"
                          onClick={() => toggleSort(`screen:${screen.id}`)}
                        >
                          <span className="screen-column-label fw-semibold">{screen.name}</span>
                          {renderSortIcon(`screen:${screen.id}`)}
                        </button>
                        <div className="screen-column-controls">
                          <button
                            type="button"
                            className={`screen-pin-btn btn btn-link p-0 ${pinned ? 'active' : ''}`}
                            onClick={() => toggleScreenPin(screen.id)}
                            aria-pressed={pinned}
                            title={pinned ? 'Unpin column' : 'Pin column'}
                          >
                            <FaThumbtack aria-hidden="true" />
                            <span className="visually-hidden">
                              {pinned ? 'Unpin' : 'Pin'} {screen.name}
                            </span>
                          </button>
                        </div>
                      </div>
                    </th>
                  );
                })}
                <th className="screen-total-header" style={{ width: '3%', minWidth: '48px', whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    className="sort-button"
                    onClick={() => toggleSort('total')}
                  >
                    T {renderSortIcon('total')}
                  </button>
                </th>
                <th className="screen-actions-header" style={{ width: '6%', minWidth: '90px', whiteSpace: 'nowrap', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={tableColumnCount} className="text-center py-3">
                    Loading...
                  </td>
                </tr>
              ) : tickers.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} className="text-center py-3">
                    No tickers added yet.
                  </td>
                </tr>
              ) : filteredTickerRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} className="text-center py-3">
                    No tickers match your filter.
                  </td>
                </tr>
              ) : (
                filteredTickerRows.map((ticker, index) => {
                  return (
                    <tr key={ticker.id}>
                      <td className="text-muted screen-index-cell">{index + 1}</td>
                      <td>{ticker.symbol}</td>
                      {screens.map((screen) => {
                        const isStarred = Boolean(stars[toKey(ticker.id)]?.[toKey(screen.id)]);
                        return (
                          <td
                            key={`${ticker.id}-${screen.id}`}
                            className="screen-value-cell"
                            style={{ width: `${screenColumnWidthPercent}%` }}
                          >
                            <button
                              type="button"
                              className={`star-toggle btn btn-link p-0 ${isStarred ? 'active' : ''}`}
                              onClick={() => toggleStar(ticker.id, screen.id)}
                              aria-pressed={isStarred}
                              aria-label={`Toggle ${screen.name} star for ${ticker.symbol}`}
                            >
                              {isStarred ? (
                                <FaStar aria-hidden="true" />
                              ) : (
                                <FaRegStar aria-hidden="true" />
                              )}
                            </button>
                          </td>
                        );
                      })}
                    <td className="screen-total-cell" style={{ width: '3%', minWidth: '48px', whiteSpace: 'nowrap' }}>{ticker.totalStars}</td>
                    <td
                      className="screen-actions-cell"
                      style={{ width: '6%', minWidth: '90px', whiteSpace: 'nowrap', textAlign: 'center' }}
                      onContextMenu={(event) => handleActionContextMenu(event, ticker)}
                      title="Right-click for actions"
                    >
                      <div className="screen-row-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success screen-row-action-btn"
                          onClick={() => handlePromoteToReadylist(ticker)}
                          title="Promote to Readylist"
                        >
                          <FaArrowUp size="0.8em" />
                          <span className="visually-hidden">Promote to Readylist</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-warning screen-row-action-btn"
                          onClick={() => handlePromoteToLeaders(ticker)}
                          title="Promote to Leaders"
                        >
                          <FaStar size="0.8em" />
                          <span className="visually-hidden">Promote to Leaders</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary screen-row-action-btn"
                          onClick={() => handleDeleteTickerRow(ticker.id)}
                          title="Delete ticker"
                        >
                          <i className="bi bi-trash"></i>
                          <span className="visually-hidden">Delete ticker</span>
                        </button>
                      </div>
                    </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {columnContextMenu.show && (
        <div
          className="screen-context-menu"
          style={{ top: `${columnContextMenu.y}px`, left: `${columnContextMenu.x}px` }}
          role="menu"
        >
          <button
            type="button"
            className="dropdown-item"
            onClick={() => {
              openScreenModal('edit', columnContextMenu.screen);
              closeColumnContextMenu();
            }}
          >
            Rename
          </button>
          <button
            type="button"
            className="dropdown-item text-danger"
            onClick={() => {
              handleDeleteScreen(columnContextMenu.screen.id);
              closeColumnContextMenu();
            }}
          >
            Delete
          </button>
        </div>
      )}

      <Modal
        show={showResetConfirm}
        onHide={closeResetConfirmation}
        centered
        dialogClassName="screen-reset-modal"
        backdrop="static"
        keyboard={!resettingStars}
        style={resetModalStyle}
      >
        <Modal.Header closeButton={!resettingStars}>
          <Modal.Title>Reset Screen Stars</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This will clear {activeStarCount} {activeStarCount === 1 ? 'star' : 'stars'} across {screens.length || 0}{' '}
            {screens.length === 1 ? 'screen' : 'screens'}. This action cannot be undone.
          </p>
          <ul className="screen-reset-summary">
            <li>
              <span className="screen-reset-summary-label">Tickers</span>
              <span className="screen-reset-summary-value">{tickers.length}</span>
            </li>
            <li>
              <span className="screen-reset-summary-label">Screens</span>
              <span className="screen-reset-summary-value">{screens.length}</span>
            </li>
            <li>
              <span className="screen-reset-summary-label">Stars</span>
              <span className="screen-reset-summary-value">{activeStarCount}</span>
            </li>
          </ul>
          <p className="screen-reset-hint mb-0">
            All star data will refresh automatically once the reset completes.
          </p>
          {resetError && <div className="screen-reset-error">{resetError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeResetConfirmation} disabled={resettingStars}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleResetStarsConfirm} disabled={resettingStars}>
            {resettingStars ? 'Resetting...' : 'Reset Stars'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={screenModal.show} onHide={closeScreenModal} centered>
        <Form onSubmit={handleScreenSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{screenModal.mode === 'edit' ? 'Rename Screen' : 'Create Screen'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="screenName">
              <Form.Label>Screen name</Form.Label>
              <Form.Control
                value={screenModal.name}
                onChange={(event) => setScreenModal((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="e.g. Growth, Dividend, Momentum"
                isInvalid={Boolean(screenError)}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">{screenError}</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={closeScreenModal}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {screenModal.mode === 'edit' ? 'Save' : 'Create'}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Screen;
